import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Building2, Home, Droplets } from "lucide-react";
import { api } from "@/lib/api";

const ICONS = { "post-construction": Building2, "move-in-out": Home, "general-deep-cleaning": Droplets, "property-detailing": Sparkles };

// Curated fallback imagery (premium interiors — never fabricated before/after results).
const FALLBACK = {
  "property-detailing": "https://images.unsplash.com/photo-1586323715429-7d4f8c5d05fd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "general-deep-cleaning": "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

// Four flagship services (premium property-detailing positioning).
const CARDS = [
  { key: "post-construction", slug: "post-construction-cleaning", title: "Post-Construction Detail", what: "Fine dust removal and a full reset after building or renovation.", who: "New builds & renovations", to: "/services/post-construction-cleaning" },
  { key: "move-in-out", slug: "move-in-move-out-cleaning", title: "Move-In / Move-Out", what: "A complete reset for an empty property, ready for the next chapter.", who: "Movers, tenants & landlords", to: "/services/move-in-move-out-cleaning" },
  { key: "general-deep-cleaning", slug: "general-deep-cleaning", title: "Deep Reset", what: "Intensive detailing that tackles built-up grime and long-skipped areas.", who: "Busy or neglected homes", to: "/services/general-deep-cleaning" },
  { key: "property-detailing", slug: "property-detailing", title: "Private Residence Detail", what: "Our highest, most meticulous surface-by-surface finish for homes that demand it.", who: "Private residences & premium homes", to: "/services/property-detailing" },
];

export const ServiceSelectCards = () => {
  const [heroBySlug, setHeroBySlug] = useState({});
  // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot load on mount
  useEffect(() => {
    api.get("/services").then(({ data }) => {
      const map = {};
      (data || []).forEach((s) => { if (s.slug) map[s.slug] = s.hero_image; });
      setHeroBySlug(map);
    }).catch(() => {});
  }, []);

  return (
    <div className="grid gap-5 sm:grid-cols-2">
      {CARDS.map((c, i) => {
        const Icon = ICONS[c.key] || Sparkles;
        const img = (c.slug && heroBySlug[c.slug]) || FALLBACK[c.key] || FALLBACK["property-detailing"];
        return (
          <Link
            key={c.key}
            to={c.to}
            data-testid={`service-selection-card-${c.key}`}
            className="svc-tile flex min-h-[22rem] flex-col justify-end p-6"
          >
            <img src={img} alt={c.title} loading="lazy" decoding="async" className="svc-media" />
            <div className="svc-overlay" />
            <span className="svc-hairline" aria-hidden="true" />
            <span className="absolute left-6 top-5 z-10 flex h-10 w-10 items-center justify-center rounded-xl bg-black/55 border border-[color:var(--border-strong)] backdrop-blur-sm">
              <Icon className="h-5 w-5 text-gold-soft" strokeWidth={1.3} />
            </span>
            <span className="absolute right-6 top-5 editorial-index select-none" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
            <div className="relative z-10">
              <p className="text-[11px] uppercase tracking-[0.16em] text-gold-soft">{c.who}</p>
              <h3 className="mt-2 display-3 text-white">{c.title}</h3>
              <p className="mt-2 text-sm text-secondary u-body max-w-[34ch]">{c.what}</p>
              <div className="mt-5 flex items-center gap-4">
                <span className="inline-flex items-center gap-2 text-sm font-medium text-white">Explore service <ArrowRight className="svc-cta-arrow h-4 w-4 text-gold-soft" /></span>
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `/contact?service=${encodeURIComponent(c.title)}`; }}
                  className="ml-auto text-xs uppercase tracking-[0.14em] text-white/60 hover:text-gold-soft transition-colors"
                  data-testid={`service-quote-${c.key}`}
                >Get a quote</button>
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
};

export default ServiceSelectCards;
